/* -------------------------------------------------------------------------------------------------
   TD Draft 2026-10-02 CART-UP / CART-UP2 — "2. Bezug mit 10% Rabatt" upsell (cart drawer + /cart).
   Markup: snippets/td-cart-cover-upsell.liquid. Loaded as a module, so it runs once per page even though the
   drawer re-inserts the script tag on every refresh.
   - Adding: POST /cart/add.js (quantity 1, property _td_cover_upsell = 1).
   - Discount (data-code, e.g. TDB8BNN7R): the cart's codes are read (/cart.js) and sent back with ours added
     (/cart/update.js; that call replaces the codes, so customer codes are kept and nothing is doubled). Ours is only
     kept when Shopify really deducts it from the new cover line and no other discount got lost; otherwise the
     previous codes are put back: the cover stays in the cart, no saving is claimed. Without the cart's codes the
     code is not touched at all.
   - Refresh: cart:change with the new cart and the sections the theme bundles (cart:prepare-bundled-sections), like
     the theme's product form, so the drawer re-renders itself; without a usable cart the theme's own cart:refresh.
     On /cart the page reloads (the theme does the same there).
   - Once add.js has answered OK the cover is in the cart: no add error any more, and no second add from the same
     upsell. After the refresh the upsell is gone (the cart has a cover in that size) and a confirmation takes its
     place and gets the focus ("Sie sparen 10,90€" only with a verified saving; "Der 10%-Rabatt konnte nicht
     angewendet werden." when the discount was promised but Shopify did not deduct it). If the drawer does not
     refresh, the upsell itself turns into that confirmation.
   - Our code left behind without effect, or without a single blanket in the cart, is taken out again by
     assets/td-cart-lines.js (loaded on every page with the cart drawer and on /cart).
   - Errors are fixed German texts (Shopify's own descriptions can be English, e.g. "Cannot find variant").
   TD Draft 2026-10-03 CART-UP3 — Felix 03.10.2026: "TRANSLATE" (show the feature abroad, all texts translated) and
   "too much space text in drawer when item is added"; the code is TDB8BNN7R now (BEZUG10 no longer exists).
   - Texts come from the snippet in the storefront language: confirmation (data-prefix / data-saved-text /
     data-failed-text) and error notes (data-error-failed / data-error-unavailable; the German constants below are
     only the fallback).
   - The confirmation is one short line: "2. Bezug hinzugefügt – Sie sparen 10,90€" (no product title, no fit).
   - Amounts in the shop's money format (themeVariables.settings.moneyFormat), never with a space before €.
   TD Draft 2026-10-05 APP-DISC — Felix 05.10.2026 (discount app SMART Functions Creator approved): the cover discount
   comes from the app ("Extra −10%", automatic after go-live). data-code is empty (section setting td_cu_discount_code),
   so no code is read, sent, redeemed or removed: one POST /cart/add.js, then the cart is read once. The confirmation
   names what the app really deducted from the new cover line ("2. Bezug hinzugefügt – Sie sparen 10,90€"); promised
   (data-deal) but nothing deducted: "… Der 10%-Rabatt konnte nicht angewendet werden." A code in data-code would still
   be redeemed as before (old technique, not to be used).
   ------------------------------------------------------------------------------------------------- */
const ADDED_KEY = 'td_cu_added';
const ADDED_MAX_AGE = 120000;
const REFRESH_TIMEOUT = 6000;
const TEXT_FAILED = 'Der Bezug konnte nicht hinzugefügt werden. Bitte versuchen Sie es erneut.';
const TEXT_UNAVAILABLE = 'Dieser Bezug ist gerade nicht lieferbar. Bitte wählen Sie einen anderen.';
const CHECK_ICON = '<svg class="td-cu-done__icon" aria-hidden="true" focusable="false" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="m3.5 8.5 3 3 6-7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

const store = {
  get(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch (error) {
      return null;
    }
  },
  set(key, value) {
    try {
      window.sessionStorage.setItem(key, value);
    } catch (error) {
      /* storage blocked: the upsell still works, only the confirmation text after the refresh is skipped */
    }
  },
  remove(key) {
    try {
      window.sessionStorage.removeItem(key);
    } catch (error) {
      /* ignore */
    }
  }
};

const routeRoot = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const isCartPage = () => !!(window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.pageType === 'cart');
const reducedMotion = () => window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const loading = (state) => document.documentElement.dispatchEvent(new CustomEvent(`theme:loading:${state}`, { bubbles: true }));
const sameCode = (a, b) => String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();

/* 1090 → "10,90€" (Felix: never a space before €). CART-UP3: the shop's money format like Liquid's | money (all
   storefronts sell in EUR), Intl only for another active currency; spaces before € (U+0020, U+00A0, U+202F) go. */
function formatMoney(cents) {
  const value = (Number(cents) || 0) / 100;
  const settings = (window.themeVariables && window.themeVariables.settings) || {};
  const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';
  const amount = (decimals, thousands, point) => {
    const [whole, fraction] = value.toFixed(decimals).split('.');
    return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? point + fraction : '');
  };
  let text = '';
  if (currency === 'EUR' && typeof settings.moneyFormat === 'string' && /\{\{\s*\w+\s*\}\}/.test(settings.moneyFormat)) {
    text = settings.moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
      switch (key) {
        case 'amount': return amount(2, ',', '.');
        case 'amount_no_decimals': return amount(0, ',', '.');
        case 'amount_no_decimals_with_comma_separator': return amount(0, '.', ',');
        case 'amount_with_apostrophe_separator': return amount(2, "'", '.');
        default: return amount(2, '.', ','); /* amount_with_comma_separator: the shop's format */
      }
    }).replace(/<[^>]*>/g, '').replace(/&nbsp;|&#160;|&#8239;/g, ' ');
  } else {
    try {
      text = new Intl.NumberFormat(document.documentElement.lang || 'de-DE', { style: 'currency', currency }).format(value);
    } catch (error) {
      text = `${amount(2, '.', ',')}€`;
    }
  }
  return text.replace(/[\s  ]+€/g, '€').trim();
}

/* CART-UP3 — one short line: "2. Bezug hinzugefügt – Sie sparen 10,90€"; when the promised discount was not
   deducted: "2. Bezug hinzugefügt. Der 10%-Rabatt konnte nicht angewendet werden."; otherwise just the prefix
   (a colon or full stop left at its end by an older setting value is dropped). */
function doneText(prefix, savedTemplate, failedText, data) {
  const head = String(prefix || '').trim().replace(/[.:]$/, '');
  if (data.saved > 0 && savedTemplate) return `${head} – ${savedTemplate.replace('{amount}', formatMoney(data.saved))}`.trim();
  if (data.failed && failedText) return `${head}. ${failedText}`.trim();
  return head;
}

/* Keep one copy of the stylesheet in <head>: the drawer replaces its own markup (and the <link> in it) on every
   cart change, the head copy keeps the styles applied in between. */
function keepStylesheet() {
  if (document.head.querySelector('link[data-td-cu-head]')) return;
  const source = document.querySelector('link[data-td-cu-css]');
  if (!source) return;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = source.href;
  link.setAttribute('data-td-cu-head', '');
  document.head.appendChild(link);
}

function drawerSectionId() {
  const section = document.querySelector('cart-drawer')?.closest('.shopify-section');
  return section ? section.id.replace('shopify-section-', '') : null;
}

/* The theme's drawer refresh needs #shopify-section-<id> with a <cart-drawer> inside, otherwise it throws. */
function drawerMarkupUsable(html, id) {
  if (typeof html !== 'string' || !html) return false;
  try {
    const section = new DOMParser().parseFromString(html, 'text/html').getElementById(`shopify-section-${id}`);
    return !!(section && section.querySelector('cart-drawer'));
  } catch (error) {
    return false;
  }
}

/* Resolves true on the next drawer refresh, false after the timeout. */
function drawerRefreshed(timeout) {
  return new Promise((resolve) => {
    let timer = 0;
    const finish = (value) => {
      document.removeEventListener('cart-drawer:refreshed', onRefreshed);
      window.clearTimeout(timer);
      resolve(value);
    };
    const onRefreshed = () => finish(true);
    timer = window.setTimeout(() => finish(false), timeout);
    document.addEventListener('cart-drawer:refreshed', onRefreshed);
  });
}

/* POST to the Ajax cart; { ok, status, json } and never throws (network error: ok false, status 0). */
async function post(path, body) {
  try {
    const response = await fetch(`${routeRoot()}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body)
    });
    const json = await response.json().catch(() => null);
    return { ok: response.ok && !!json, status: response.status, json };
  } catch (error) {
    return { ok: false, status: 0, json: null };
  }
}

async function getCart() {
  try {
    const response = await fetch(`${routeRoot()}cart.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) return null;
    const cart = await response.json();
    return cart && Array.isArray(cart.items) && Array.isArray(cart.discount_codes) ? cart : null;
  } catch (error) {
    return null;
  }
}

/* Sets exactly these codes (the call replaces the cart's codes); the cart with the bundled sections, or null. */
async function setCodes(codes, sections) {
  const body = { discount: codes.join(',') };
  if (sections && sections.length) {
    body.sections = sections.join(',');
    body.sections_url = window.location.pathname;
  }
  const result = await post('cart/update.js', body);
  return result.ok && Array.isArray(result.json.items) && Array.isArray(result.json.discount_codes) ? result.json : null;
}

/* What our code deducts from the cover line(s) this upsell added (cents). */
function coverSaving(cart, variantId, code) {
  let saved = 0;
  cart.items.forEach((item) => {
    if (Number(item.variant_id) !== variantId || !item.properties || !item.properties._td_cover_upsell) return;
    (item.line_level_discount_allocations || []).forEach((allocation) => {
      if (allocation.discount_application && sameCode(allocation.discount_application.title, code)) saved += Number(allocation.amount) || 0;
    });
  });
  return saved;
}

/* APP-DISC: everything deducted from the cover line(s) this upsell added, whatever the discount is called (cents). */
function lineSaving(cart, variantId) {
  let saved = 0;
  ((cart && cart.items) || []).forEach((item) => {
    if (Number(item.variant_id) !== variantId || !item.properties || !item.properties._td_cover_upsell) return;
    (item.line_level_discount_allocations || []).forEach((allocation) => {
      saved += Number(allocation.amount) || 0;
    });
  });
  return saved;
}

/* Redeems the code after the cover was added. Returns { cart, saved }: cart = the cart after the last code change
   (with sections) or null when nothing was changed / the state is unknown; saved > 0 only when verified. */
async function redeem(code, variantId, sections) {
  const before = await getCart(); /* read after add.js: holds the new cover line already */
  if (!before) return { cart: null, saved: 0 }; /* codes unknown: never overwrite them */

  const previous = before.discount_codes.map((entry) => entry.code).filter(Boolean);
  const hadOurs = previous.some((entry) => sameCode(entry, code));
  const after = await setCodes(hadOurs ? previous : previous.concat(code), sections);
  if (!after) return { cart: null, saved: 0 };

  const saved = coverSaving(after, variantId, code);
  const ours = after.discount_codes.find((entry) => sameCode(entry.code, code));
  const othersKept = before.discount_codes
    .filter((entry) => entry.applicable && !sameCode(entry.code, code))
    .every((entry) => after.discount_codes.some((now) => sameCode(now.code, entry.code) && now.applicable));
  /* Every other deduction is still there. The new line is left out on both sides: with our code already in the cart
     (second blanket and cover) Shopify discounts it during add.js, so "before" holds its saving too. */
  const savedBefore = coverSaving(before, variantId, code);
  const nothingLost = (Number(after.total_discount) || 0) - saved >= (Number(before.total_discount) || 0) - savedBefore;
  if (ours && ours.applicable && saved > 0 && othersKept && nothingLost) return { cart: after, saved };

  /* Not deducted, or another discount gave way: back to the codes the cart had (ours only if it already worked). */
  const oursWorkedBefore = before.discount_codes.some((entry) => sameCode(entry.code, code) && entry.applicable);
  const restore = oursWorkedBefore ? previous : previous.filter((entry) => !sameCode(entry, code));
  const reverted = (await setCodes(restore, sections)) || (await setCodes(restore, sections));
  return { cart: reverted, saved: 0 };
}

class TdCartCoverUpsell extends HTMLElement {
  connectedCallback() {
    keepStylesheet();
    if (!this._wired) {
      this._wired = true;
      this.addEventListener('click', (event) => this.onClick(event));
    }
    this.setupCarousel();
  }

  disconnectedCallback() {
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
      this._resizeObserver = null;
    }
  }

  onClick(event) {
    const addButton = event.target.closest('[data-td-cu-add]');
    if (addButton && this.contains(addButton)) {
      event.preventDefault();
      this.add(addButton);
      return;
    }
    const nav = event.target.closest('[data-td-cu-nav]');
    if (nav && this.contains(nav)) {
      event.preventDefault();
      this.scrollTiles(nav);
    }
  }

  /* Arrows for mouse users (touch scrolls natively) */
  setupCarousel() {
    const list = this.querySelector('[data-td-cu-tiles]');
    const wrap = this.querySelector('[data-td-cu-navwrap]');
    if (!list || !wrap) return;
    const update = () => {
      const overflowing = list.scrollWidth > list.clientWidth + 4;
      wrap.hidden = !overflowing;
      if (!overflowing) return;
      const [previous, next] = wrap.querySelectorAll('[data-td-cu-nav]');
      previous.setAttribute('aria-disabled', list.scrollLeft <= 2 ? 'true' : 'false');
      next.setAttribute('aria-disabled', list.scrollLeft + list.clientWidth >= list.scrollWidth - 2 ? 'true' : 'false');
    };
    if (!this._carouselWired) {
      this._carouselWired = true;
      list.addEventListener('scroll', update, { passive: true });
    }
    if (!this._resizeObserver && 'ResizeObserver' in window) {
      this._resizeObserver = new ResizeObserver(update);
      this._resizeObserver.observe(list);
    }
    update();
  }

  scrollTiles(button) {
    if (button.getAttribute('aria-disabled') === 'true') return;
    const list = this.querySelector('[data-td-cu-tiles]');
    if (!list) return;
    const direction = Number(button.getAttribute('data-td-cu-nav')) || 1;
    list.scrollBy({ left: direction * Math.max(list.clientWidth * 0.8, 120), behavior: reducedMotion() ? 'auto' : 'smooth' });
  }

  /* The message sits under the covers; in the drawer that can be below the visible part, so it is scrolled into
     view (the drawer body scrolls above its footer). role="alert" announces it, the focus stays on the button. */
  setError(message) {
    const error = this.querySelector('[data-td-cu-error]');
    if (!error) return;
    error.textContent = message || '';
    error.hidden = !message;
    if (message) error.scrollIntoView({ block: 'nearest', behavior: reducedMotion() ? 'auto' : 'smooth' });
  }

  release(button) {
    this._busy = false;
    button.removeAttribute('aria-busy');
  }

  async add(button) {
    if (this._busy || this._added) return;
    const variantId = Number(button.dataset.variantId);
    if (!variantId) return;
    this._busy = true;
    this.setError('');
    button.setAttribute('aria-busy', 'true');
    loading('start');

    const onCartPage = isCartPage();
    const code = (this.dataset.code || '').trim();
    const sections = [];
    if (!onCartPage) {
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));
    }

    /* With a code the drawer markup comes with the code change, otherwise with the add itself */
    const body = { items: [{ id: variantId, quantity: 1, properties: { _td_cover_upsell: '1' } }] };
    if (!code && sections.length) {
      body.sections = sections.join(',');
      body.sections_url = window.location.pathname;
    }
    const added = await post('cart/add.js', body);
    if (!added.ok) {
      loading('end');
      this.release(button);
      /* 422 = Shopify refused this variant (sold out meanwhile, or gone); CART-UP3: texts in the storefront language */
      this.setError(added.status === 422 ? this.dataset.errorUnavailable || TEXT_UNAVAILABLE : this.dataset.errorFailed || TEXT_FAILED);
      return;
    }

    /* From here on the cover is in the cart: only the discount and the refresh are left, a second click must not add
       another one. */
    this._added = true;
    let cart = null;
    let saved = 0;
    let known = false;
    if (code) {
      ({ cart, saved } = await redeem(code, variantId, sections));
      known = true;
    } else {
      /* APP-DISC: no code — the app discount is already in the cart after add.js; read what it took off the cover */
      cart = await this.cartWithSections(added.json, sections);
      const fresh = cart || (await getCart());
      if (fresh) {
        saved = lineSaving(fresh, variantId);
        known = true;
      }
    }
    loading('end');

    /* failed: the tiles promised the discount (data-deal) but Shopify did not deduct it; the cover stays (unknown cart:
       no claim either way) */
    const failed = known && this.hasAttribute('data-deal') && !(saved > 0);
    const data = { name: button.dataset.name || '', fit: this.dataset.fit || '', saved, failed, t: Date.now() };
    store.set(ADDED_KEY, JSON.stringify(data));

    if (onCartPage) {
      window.location.reload();
      return;
    }

    const refreshed = drawerRefreshed(REFRESH_TIMEOUT);
    if (!this.publishCart(cart, sections)) {
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    }
    await refreshed;
    if (!this.isConnected) return; /* the drawer re-rendered: its confirmation box takes over */
    this.release(button);
    this.showInlineDone(data);
  }

  /* add.js without a code: its sections plus the full cart (the theme's listeners need both). */
  async cartWithSections(json, sections) {
    const rendered = json && json.sections;
    if (!rendered || !sections.length) return null;
    const cart = await getCart();
    if (!cart) return null;
    cart.sections = rendered;
    return cart;
  }

  /* Hands the new cart to the theme like its product form does (cart:change with the bundled sections, the drawer
     re-renders from them). Returns false when the cart or the drawer markup is missing, so the caller can fall
     back to the theme's cart:refresh; never throws. */
  publishCart(cart, sections) {
    try {
      const rendered = cart && cart.sections;
      const drawerId = drawerSectionId();
      if (!cart || !Array.isArray(cart.items) || !rendered || !sections.length) return false;
      if (sections.some((id) => typeof rendered[id] !== 'string' || !rendered[id])) return false;
      if (drawerId && sections.includes(drawerId) && !drawerMarkupUsable(rendered[drawerId], drawerId)) return false;
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart } }));
      return true;
    } catch (error) {
      return false;
    }
  }

  /* The drawer did not re-render in time: the cover is in the cart all the same, so the upsell gives way to the
     confirmation right here (no second add by mistake). The next drawer refresh shows the cart line itself. */
  showInlineDone(data) {
    Array.from(this.children).forEach((child) => {
      child.hidden = true;
    });
    const box = document.createElement('p');
    box.className = 'td-cu-done td-cu-done--inline';
    box.setAttribute('role', 'status');
    box.tabIndex = -1;
    box.innerHTML = CHECK_ICON;
    const text = document.createElement('span');
    text.textContent = doneText(this.dataset.addedPrefix, this.dataset.savedText, this.dataset.failedText, data);
    box.append(text);
    this.append(box);
    box.focus();
  }
}

if (!window.customElements.get('td-cart-cover-upsell')) {
  window.customElements.define('td-cart-cover-upsell', TdCartCoverUpsell);
}

/* Confirmation after adding: the snippet renders a hidden status box once the cart holds a line with
   _td_cover_upsell; it is filled and focused right after the drawer refresh or the /cart reload. */
function showAdded(context) {
  const raw = store.get(ADDED_KEY);
  if (!raw) return;
  let data = null;
  try {
    data = JSON.parse(raw);
  } catch (error) {
    data = null;
  }
  if (!data || Date.now() - (data.t || 0) > ADDED_MAX_AGE) {
    store.remove(ADDED_KEY);
    return;
  }
  const box = Array.from(document.querySelectorAll('[data-td-cu-done]')).find((element) => element.getAttribute('data-context') === context);
  if (!box) return;
  store.remove(ADDED_KEY);
  const text = box.querySelector('[data-td-cu-done-text]');
  if (text) text.textContent = doneText(box.getAttribute('data-prefix'), box.getAttribute('data-saved-text'), box.getAttribute('data-failed-text'), data);
  box.hidden = false;
  window.requestAnimationFrame(() => box.focus({ preventScroll: false }));
}

if (!window.__tdCartCoverUpsell) {
  window.__tdCartCoverUpsell = true;
  keepStylesheet();
  document.addEventListener('cart-drawer:refreshed', () => showAdded('drawer'));
  if (isCartPage()) showAdded('page');
}
